import {
  type ViewMode,
  viewModeEquals,
  FeatureToggleCapabilityCoordinator,
  PaneVisibilityCoordinator,
  ViewModeCapabilityCoordinator,
} from '@mobile-mapping/web3d';
import type { CapabilityRegistry } from '@trimble/web3d';
import type { PaneLayouts } from '@trimble/pane-layout';
import type { SetStoreFunction } from 'solid-js/store';
import { waitForReactive } from '@trimble/solidjs-utils';
import type {
  MultipaneCellContentConfig,
  MultipaneReservedData,
} from './multipane-view.types';

const VIEW_LOAD_TIMEOUT_MS = 15_000;

export function applyMultipaneLayout(
  layouts: PaneLayouts<ViewMode>,
  setLayouts: SetStoreFunction<PaneLayouts<ViewMode>>,
  reserved: MultipaneReservedData | null
): void {
  if (!reserved?.layout?.paneRows) {
    if (layouts.activeLayoutLabel !== 'Single') {
      setLayouts('activeLayoutLabel', 'Single');
    }
    return;
  }

  const savedLayout = reserved.layout;
  const existingIdx = layouts.layouts.findIndex(
    (l) => l.label === savedLayout.label
  );

  const viewModeLayout = {
    label: savedLayout.label,
    paneRows: savedLayout.paneRows.map((row) => ({
      config: row.config,
      cells: row.cells.map((cell) => ({
        size: cell.size,
        minSize: cell.minSize,
        contentConfig: cell.contentConfig.viewMode,
      })),
    })),
  };

  if (existingIdx >= 0) {
    setLayouts('layouts', existingIdx, viewModeLayout);
  } else {
    setLayouts('layouts', layouts.layouts.length, viewModeLayout);
  }
  if (layouts.activeLayoutLabel !== savedLayout.label) {
    setLayouts('activeLayoutLabel', savedLayout.label);
  }
}

export async function applyPaneState(
  capabilityRegistry: CapabilityRegistry,
  reserved: MultipaneReservedData | null
): Promise<void> {
  if (!reserved?.layout?.paneRows) return;

  // Pane containers and their coordinators register reactively after
  // `applyMultipaneLayout`, so each cell waits for its own registration
  // before applying the saved view-mode and feature-toggle state.
  const pending = reserved.layout.paneRows.flatMap((row, rowIdx) =>
    row.cells.map((cell, cellIdx) =>
      applyCellState(capabilityRegistry, rowIdx, cellIdx, cell.contentConfig)
    )
  );
  await Promise.all(pending);
}

async function applyCellState(
  capabilityRegistry: CapabilityRegistry,
  rowIdx: number,
  cellIdx: number,
  contentConfig: MultipaneCellContentConfig
): Promise<void> {
  const pane = await waitForReactive(
    () =>
      capabilityRegistry
        .paneContainers()
        .find((p) => p.paneRowIdx === rowIdx && p.paneCellIdx === cellIdx),
    (p) => p !== undefined,
    VIEW_LOAD_TIMEOUT_MS
  ).catch(() => undefined);
  if (!pane) return;

  const vmCoord = await waitForReactive(
    () => pane.get(ViewModeCapabilityCoordinator)(),
    (c) => c !== undefined,
    VIEW_LOAD_TIMEOUT_MS
  ).catch(() => undefined);
  if (vmCoord && !viewModeEquals(vmCoord.viewMode(), contentConfig.viewMode)) {
    vmCoord.setViewMode(contentConfig.viewMode);
  }

  const ftCoord = await waitForReactive(
    () => pane.get(FeatureToggleCapabilityCoordinator)(),
    (c) => c !== undefined,
    VIEW_LOAD_TIMEOUT_MS
  ).catch(() => undefined);

  // A feature-toggle timeout must not skip pane-visibility restoration:
  // PaneVisibilityCoordinator registers independently and may be ready
  // even when the feature-toggle coordinator is not.
  if (ftCoord) {
    await ftCoord.applyFeatureToggleStateReactively(
      contentConfig.featureToggles,
      VIEW_LOAD_TIMEOUT_MS
    );
  }

  // Legacy views won't carry `paneVisibility`; in that case the
  // pane keeps its defaults (linked=true, local=null, clipDistance follows
  // upstream). Newer views restore each linkable slider/range row to the
  // exact snapshot it had at save time.
  if (contentConfig.paneVisibility) {
    const paneVisCap = await waitForReactive(
      () => pane.get(PaneVisibilityCoordinator)(),
      (c) => c !== undefined,
      VIEW_LOAD_TIMEOUT_MS
    ).catch(() => undefined);
    paneVisCap?.applySnapshot(contentConfig.paneVisibility);
  }
}
